//Hooks
import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//ChartJS
import { Chart, registerables } from 'chart.js';
//Utilities
import { math_circular, sum } from '../../../helpers/utilities';
//ChartPlugins
import { chartPluginDefault, msgNoDataPlugin, pieLabelsPlugin } from './chartPlugins';
//Helpers
import { dateFormat, localTimeToUTC, timeFormatUS } from '../../../helpers/date_helper';
import {
	formatSeriesLabel,
	getTimeLabelFormat,
	getSystemStyle,
	getClickAction,
	CHART_VARIANTS_LIST,
	getRangeValue,
	MAPPED_CHART_TYPE_INDEXES,
	createChartTooltip,
	checkForStatisticError,
} from '../chart_helper';
import {
	createChartLabelGradient,
	getChartColorStyle,
	getGradientColor,
	getIntervalAvatarColors,
} from '../chart_colors_helpers';
import { convertToUnit, skipUnitConversion } from 'src/helpers/units_helpers';
// Design system components
import { SystemStyles } from '@Shelly/shelly-design-system';
//Types
import type { ChartConfigSeriesData, ChartDataInterface, ChartInterface } from '../../../types/Chart';
import type { UpdateMode, ChartData, ScriptableContext, ChartType, ActiveDataPoint } from 'chart.js';
import type { ChartGradientColorType } from '../chart_colors_helpers';
import { formatDistanceUnit } from 'src/helpers/distance_helper';

Chart.register(...registerables);

export const chart_name = 'chartJS';
const gradientCache = {};
export class ChartUtil implements ChartInterface {
	private container: HTMLElement;
	private chart: Chart;
	private chartData: ChartDataInterface;
	private currentTypeIndex: number;
	private types = CHART_VARIANTS_LIST;
	private canvas: HTMLCanvasElement;
	private ctx: CanvasRenderingContext2D;
	private chartOptions: any;
	private clickProp = { offsetTime: 0, offsetX: 0 };
	private clickAction: string | boolean = false;
	private pieLegend = null;

	constructor(container: HTMLElement, chartData: ChartDataInterface) {
		this.currentTypeIndex = math_circular(
			MAPPED_CHART_TYPE_INDEXES[chartData.type],
			this.types.length,
		);

		this.chartData = chartData;
		this.canvas = document.createElement('canvas') as HTMLCanvasElement;
		this.canvas.style.maxHeight = '100%';
		this.canvas.height = chartData.height;
		this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;
		this.container = container;
		this.container.insertAdjacentElement('beforeend', this.canvas);
		this.chartOptions = this.constructOption(this.chartData);
		this.chart = new Chart(this.ctx, this.chartOptions);
		if (chartData.name != 'dynamicChart') {
			this.showSingleToolTip();
			this.regClickEvent();
		}
	}

	public changeType(typeIndex: number = null): number {
		if (typeIndex !== null) {
			this.currentTypeIndex = typeIndex;
		} else {
			this.currentTypeIndex = math_circular(this.currentTypeIndex + 1, this.types.length);
		}
		this.chart.destroy();
		this.chartOptions = this.constructOption(this.chartData);
		this.chart = new Chart(this.ctx, this.chartOptions);
		this.showSingleToolTip();
		return this.currentTypeIndex;
	}

	public destroy(): void {
		this.chart.destroy();
		document.removeEventListener('touchstart', this.clickOutside);
		this.canvas.removeEventListener('touchstart', this.onClick);
		this.canvas.removeEventListener('mousedown', this.onClick);
		if (this.container.contains(this.canvas)) {
			this.container.removeChild(this.canvas);
		}
	}

	public getTypes(): Array<string> {
		return this.types;
	}

	public updateData(chartData: ChartDataInterface): void {
		const type =
			chartData.name == 'dynamicChart' ? chartData.type : this.types[this.currentTypeIndex];
		const data = this.setUpData(chartData, type);
		if (type != 'pie') {
			const scales = this.chart.config.options.scales;
			// if (scales.y.min > data.minMax.min || scales.y.max < data.minMax.max) {
			if (scales.y.min != data.minMax.min || scales.y.max != data.minMax.max) {
				this.chart.config.options.scales = this.setScaleOption(data);
			}
		}
		if (this.chart.data.labels.join() != chartData.labels.join()) {
			this.chart.data = data.data;
			if (type == 'pie' && this.pieLegend) {
				for (let i = 0; i < this.chart.data.labels.length; ++i) {
					if (!this.chart.getDataVisibility(i)) {
						this.chart.toggleDataVisibility(i);
					}
				}
				for (const legend in this.pieLegend) {
					const index = chartData.labels.indexOf(legend);
					if (index >= 0) this.chart.toggleDataVisibility(index);
				}
			}
			this.update();
		} else {
			const datasets = data.data.datasets,
				datasetsLen = datasets.length;
			if (datasetsLen == this.chart.data.datasets.length) {
				for (let i = 0; i < datasetsLen; ++i) {
					this.chart.data.datasets[i].data = datasets[i].data;
				}
			}
			this.update('default');
		}
	}

	private update(mode: UpdateMode = 'none'): void {
		this.chart.update(mode);
	}
	private regClickEvent() {
		if (!this.clickAction) return;
		this.canvas.addEventListener(
			'ontouchstart' in window ? 'touchstart' : 'mousedown',
			this.onClick,
		);
	}
	private onClick = (e: TouchEvent | MouseEvent) => {
		if (e instanceof MouseEvent || (e instanceof TouchEvent && e.touches.length == 1)) {
			//@ts-ignore
			const offsetX =
				window.TouchEvent && e instanceof TouchEvent
					? e.touches[0].pageX
					: //@ts-ignore
						e.pageX;
			if (
				this.clickProp.offsetX > 0 &&
				e.timeStamp - this.clickProp.offsetTime < 250 &&
				Math.abs(this.clickProp.offsetX - offsetX) < 15
			) {
				this.chartClickAction(e);
			} else {
				this.clickProp = { offsetTime: e.timeStamp, offsetX };
			}
		}
	};
	private chartClickAction(e: Event) {
		const point = this.chart.getElementsAtEventForMode(
			e,
			'nearest',
			{
				intersect: false,
				axis: 'x',
			},
			true,
		)[0];
		if (point) {
			const dataIndex = point.index,
				date = localTimeToUTC(this.chart.data.labels[dataIndex] as any),
				datasetIndex = point.datasetIndex,
				value = this.chart.data.datasets[datasetIndex].data[dataIndex];
			this.container.dispatchEvent(
				new CustomEvent('change', {
					detail: {
						date,
						value,
						action: 'dblClick',
						clickAction: this.clickAction,
					},
				}),
			);
		}
	}
	private showSingleToolTip() {
		document.removeEventListener('touchstart', this.clickOutside);
		const datasets = this.chartOptions.data.datasets;
		if (datasets.length && this.chartOptions.type != 'bar') {
			const singleDataset = this.chartOptions.data.datasets[0].data.filter((i) => i != null);
			if (this.chart.tooltip && singleDataset.length == 1) {
				const index = datasets[0].data.indexOf(singleDataset[0]),
					chartArea = this.chart.chartArea,
					activePoints: ActiveDataPoint[] = [];
				datasets.map((el: any, i: number) => {
					if (!el.hidden) {
						activePoints.push({
							datasetIndex: i,
							index,
						});
					}
				});
				if (!this.chartData.thumbMode) {
					this.chart.tooltip.setActiveElements(activePoints, {
						x: (chartArea.left + chartArea.right) / 2,
						y: (chartArea.top + chartArea.bottom) / 2,
					});
				}
				this.chart.setActiveElements(activePoints);
				this.chart.options.events = [];
				this.update();
			}
		}
		// Setting this in an "else if" check will disable "clickOutside" for charts other than bar charts.
		if ('ontouchstart' in window) {
			document.addEventListener('touchstart', this.clickOutside);
		}
	}
	private clickOutside = (e) => {
		if (!this.canvas.contains(e.target)) {
			this.canvas.dispatchEvent(new Event('mouseout'));
		}
	};
	private constructOption(chartData: ChartDataInterface) {
		const type =
				chartData.name == 'dynamicChart' ? chartData.type : this.types[this.currentTypeIndex],
			data = this.setUpData(chartData, type),
			convertToUnitParams = {
				fixed: 1,
				dontConvert: skipUnitConversion(chartData.title),
			},
			options = {
				title: '',
				data: data.data,
				type: type.startsWith('area') ? 'line' : type,
				responsive: true,
				plugins: [chartPluginDefault, msgNoDataPlugin, pieLabelsPlugin],
				options: {
					responsive: true,
					maintainAspectRatio: false,
					...(type == 'bar' && {
						animation: {
							onProgress: function (animation) {
								animation.chart.update('none');
							},
						},
					}),
					interaction:
						type == 'pie'
							? {}
							: {
									intersect: false,
									mode: 'index',
									axis: 'x',
								},
					elements: {
						point: {
							hoverRadius: 6,
							hoverBorderColor: getSystemStyle(SystemStyles.COLOR_WHITE),
							hoverBorderWidth: 4,
						},
						line: {
							borderCapStyle: 'round',
							borderJoinStyle: 'round',
							tension: /^(monthlyReport)$/.test(chartData.name) ? 0 : 0.4,
						},
					},
					plugins: {
						...(type == 'pie' && {
							pieLabels: {
								fontSize: 12,
								color: getSystemStyle(SystemStyles.COLOR_WHITE),
								units: chartData.units,
							},
						}),
						legend: {
							display:
								chartData.type == 'pie' ||
								(!/^(tempSchedule)$/.test(chartData.name) &&
									!chartData.thumbMode &&
									(data.data.datasets.length > 1 ||
										chartData.series.find((i) => i.prevPeriod === true) != null)),
							position: 'bottom',
							labels: {
								font: {
									size: 10,
									family: getSystemStyle(SystemStyles.FONT_FAMILY),
									weight: 400,
								},
								usePointStyle: true,
								color: getSystemStyle(SystemStyles.COLOR_WHITE),
								pointStyle: 'circle',
								boxWidth: 8,
								boxHeight: 8,
								padding: 8,
								borderWidth: 0,
								...(type != 'pie' && {
									generateLabels: (chart) => {
										return chart.data?.datasets.map((dataset, i) => {
											return {
												datasetIndex: i,
												text: dataset.label,
												fillStyle: createChartLabelGradient(
													chart['$context'],
													dataset.labelColor,
												),
												fontColor: getSystemStyle(SystemStyles.COLOR_WHITE),
												strokeStyle: 'transparent',
												hidden: !chart.isDatasetVisible(i),
											};
										});
									},
								}),
							},
							onClick: (_e, legendItem, legend) => {
								const chart = legend.chart;
								if (type != 'pie') {
									const index = legendItem.datasetIndex,
										hidden = chart.isDatasetVisible(index);
									chart.setDatasetVisibility(index, !hidden);
									legendItem.hidden = hidden;
									let seriesIndex = index;
									if (
										this.chart.config?.data?.datasets?.[index]?.['labelIndex'] !=
										null
									) {
										seriesIndex =
											this.chart.config.data.datasets[index]['labelIndex'];
									}
									if (chartData.name != 'dynamicChart') {
										chartData.series[seriesIndex].visible = !hidden;
										const data = this.setUpData(chartData, type),
											scales = this.chart.config.options.scales;
										if (
											scales.y.min != data.minMax.min ||
											scales.y.max != data.minMax.max
										) {
											this.chart.config.options.scales = this.setScaleOption(data);
											chart.update('reset');
										}
									}
									this.container.dispatchEvent(
										new CustomEvent('change', {
											detail: {
												action: 'toggleLegend',
												index: seriesIndex,
												visible: !hidden,
											},
										}),
									);
								} else {
									const index = legendItem.index;
									legend.chart.toggleDataVisibility(index);
									if (!this.pieLegend) this.pieLegend = {};
									if (!legendItem.hidden) {
										this.pieLegend[legendItem.text] = 1;
									} else {
										delete this.pieLegend[legendItem.text];
									}
								}
								//Hide the tooltip on a legend click
								this.canvas.dispatchEvent(new Event('mouseout'));
								chart.update();
							},
						},
						filler: {
							drawTime: type.startsWith('area')
								? 'beforeDatasetsDraw'
								: 'beforeDatasetDraw',
						},
						tooltip: {
							enabled: false,
							external: createChartTooltip,
							callbacks: {
								labelColor: function (data) {
									const index = data.dataIndex;
									let borderColorSolid = getIntervalAvatarColors(
										chartData.series[0].data[index]?.bgColor,
									)?.solid;

									if (borderColorSolid) return { backgroundColor: borderColorSolid };

									if (data.dataset.tooltipIndicatorColor) {
										return {
											backgroundColor: data.dataset.tooltipIndicatorColor,
										};
									}
								},
								label: function (context) {
									if (context.dataset.errorDatasets[context.dataIndex]) {
										return 'N/A';
									}
									if (type == 'pie') {
										return `<div class="legend"><ib>${convertToUnit(
											context.dataset.data[context.dataIndex],
											chartData.units,
											convertToUnitParams,
										)}</div>`;
									}
									let value = context.parsed.y || 0;
									if (value > 0 && value < 0.01) value = 0.01;
									let suffixText = '',
										additionalData = [];
									if (chartData.name == 'tariffIntervals') {
										let metaData = context.dataset.metaData[context.dataIndex];
										if (metaData) metaData = `<b>${metaData}</b>`;
										return [
											`<div class="values" style="margin-left:-10px"><ic>${convertToUnit(value, chartData.units, convertToUnitParams)}</div>`,
											metaData,
										];
									} else if (
										!/(reversed|voltage)/.test(context.dataset.name) &&
										context.dataset.consumptionCost?.length &&
										context.dataset.consumptionCost?.[context.dataIndex]
									) {
										suffixText = `<ic>${context.dataset.consumptionCost[context.dataIndex]}`;
										if (
											/consumption/.test(chartData.name) &&
											context.dataset?.metaData?.length &&
											context.dataset.metaData?.[context.dataIndex]
										) {
											const metaData =
												context.dataset?.metaData[context.dataIndex];
											for (let i = 0; i < metaData.length; ++i) {
												const item = metaData[i];
												if (item) {
													additionalData.push(
														`<div class="tariff">${item?.tariff?.name}</div><div class="values"><ib>${item.value}<ic>${item.cost}</div>`,
													);
												}
											}
										}
									}
									let result = '';
									if (/^(w|wh)$/i.test(chartData.units)) {
										let date = '';
										if (/consumption/.test(chartData.name)) {
											if (context.dataset.prevValues?.length) {
												date = dateFormat(
													Number(
														context.dataset.prevValues[context.dataIndex],
													),
													getTimeLabelFormat(
														/^(day|custom|billing_date)$/.test(
															chartData.rangeValue,
														)
															? 'month'
															: chartData.rangeValue,
													),
													true,
												);
											} else {
												date = dateFormat(
													Number(context.label),
													getTimeLabelFormat(chartData.rangeValue),
													true,
												);
											}
											date += ' - ';
										}
										result =
											`<div class="legend">${date}${context.dataset.label}</div><div class="values text-bold">` +
											(/(reversed|returned|generated|export_energy)/.test(
												context.dataset.name,
											)
												? '<ih>'
												: '<ib>') +
											convertToUnit(
												Math.abs(value),
												chartData.units,
												convertToUnitParams,
											) +
											suffixText;
									} else {
										const label = /^(value|ppm)$/i.test(context.dataset.label)
											? ''
											: `<div class="legend">${context.dataset.label}</div>`;
										result = `${context.dataset.label !== 'TempSchedule' ? label : ''}<div class="values text-bold">${formatDistanceUnit(
											convertToUnit(value, chartData.units, {
												...convertToUnitParams,
												fixed: Math.abs(value) < 0.1 ? 2 : 1,
											}),
											chartData.deviceType,
										)}${suffixText}</div></div>`;
									}
									if (additionalData.length) {
										return [result, ...additionalData];
									}
									return result;
								},
								title: function (context) {
									if (chartData.labels) return context[0].label;
									let label = context[0].label || '';
									if (label) {
										const labelFormats = /^tempSchedule|tariffIntervals$/.test(
											chartData.name,
										)
											? 'tariff_day_of_week'
											: chartData.rangeValue;
										label = dateFormat(
											Number(label),
											getTimeLabelFormat(labelFormats),
											true,
										);
									}
									return label;
								},
							},
							...(chartData.deviceMode != 'triphase' &&
								/^(pv_setup_consumption|temperature|voltage|voltmeter|input)$/.test(
									chartData.name,
								) && {
									itemSort: function (a, b) {
										// Sort tooltip values to show max value first
										return b.datasetIndex - a.datasetIndex;
									},
								}),
						},
					},
					layout: {
						padding: {
							top: type == 'pie' ? 15 : 20,
							bottom: type == 'pie' ? 3 : -3,
							right: type == 'bar' ? 5 : 0,
						},
					},
					scales: type == 'pie' ? {} : this.setScaleOption(data),
				},
			};
		return options;
	}
	private calcTickInterval(
		min: number,
		max: number,
		type: string,
	): { min: number; max: number; stepSize: number } {
		let stepSize = 1;
		if ((min == 0 && max == 0) || min == Number.MAX_SAFE_INTEGER || max == Number.MIN_SAFE_INTEGER) {
			min = type == 'bar' ? 0 : -1;
			stepSize = type == 'bar' ? 0.5 : 1;
			max = 1;
		} else if (min == max) {
			min = Math.round(min - 1);
			max = Math.round(max + 1);
		} else {
			min = Math.floor(min);
			max = Math.ceil(max);
			if (type == 'bar' && min < 0 && max <= 0) {
				max = 0;
			}
			const diff = Math.abs(max - min);
			if (diff == 1) {
				stepSize = 0.5;
			} else if (diff >= 10) {
				const nearest = diff >= 100 ? (diff >= 1000 ? 1000 : 100) : 10;
				min = Math.floor(min / nearest) * nearest;
				max = Math.ceil(max / nearest) * nearest;
				// When generating an energy chart, the y-scale shows mirrored positive and negative scaled values.
				// if (min < 0 && max > 0) max > Math.abs(min) ? min = max * -1 : max = min * -1;
				stepSize = Math.round(Math.abs(max - min) / 2);
			} else {
				if (diff == 7) {
					stepSize = max == 0 ? 4 : min + 4;
				} else if (diff == 9) {
					stepSize = max == 0 ? 5 : min + 5;
				} else {
					stepSize = diff != 2 && diff % 2 == 0 ? 2 : 1;
				}
			}
		}
		return { min, max, stepSize };
	}
	private showErrorPoint(data): boolean {
		return data.dataset.errorDatasets[data.dataIndex] != null;
	}
	private setUpData(
		chartData: ChartDataInterface,
		type: string,
	): {
		minMax: { min: number; max: number; stepSize: number };
		data: ChartData;
	} {
		let min = type == 'bar' ? 0 : Number.MAX_SAFE_INTEGER,
			max = Number.MIN_SAFE_INTEGER,
			labels: (number | string)[] = [],
			datasets: any[] = [];
		if (chartData.series) {
			if (chartData.name == 'dynamicChart') {
				labels = chartData.labels;
			} else {
				if (chartData.rangeValue == 'custom') {
					chartData.rangeValue = getRangeValue(chartData.rangeInterval);
					this.clickAction = chartData.rangeInterval;
				} else {
					this.clickAction = getClickAction(chartData.rangeValue);
				}
			}
			const seriesLen = chartData.series.length;
			const stackDataset: {
				[key: string]: number;
			} = {};
			for (let i = 0; i < seriesLen; ++i) {
				const item = chartData.series[i],
					data: ChartConfigSeriesData[] = item?.data,
					dataLen = data?.length,
					values: number[] = [],
					prevValues: number[] = [],
					metaData: any[] = [],
					errorDatasets: number[] = [],
					consumptionCost: string[] = [];
				let itemMin = Number.MAX_SAFE_INTEGER,
					itemMax = Number.MIN_SAFE_INTEGER;
				for (let d = 0; d < dataLen; ++d) {
					let value = 0;
					if (chartData.name == 'dynamicChart') {
						//@ts-ignore
						value = data[d];
					} else {
						if (data[d]?.dt !== undefined) {
							if (i == 0) labels.push(data[d].dt);
							value = data[d].value;
							const meta = data[d].metaData;
							if (chartData.name == 'consumption') {
								if (data[d].prevValue) {
									prevValues.push(data[d].prevValue);
								}
								// Hide unavailable consumption data
								if (
									data[d].missing === true &&
									(d == 0 || data[d - 1]?.missing === true)
								) {
									// value = null;
								}
							}
							if (chartData.name == 'tariffIntervals') {
								metaData.push(data[d].name);
							} else if (/consumption/.test(chartData.name) && meta && meta?.length > 0) {
								metaData.push(meta);
							}
							consumptionCost.push(data[d].convertCost);
						} else {
							if (i == 0) labels.push(data[d][0]);
							value = data[d][1];
						}
					}
					// Hide the value when using a bar chart type,
					// as the bars have a minimum height to visualize tiny consumption.
					if (type == 'bar' && value === 0) {
						value = null;
					} else if (
						/(valve_position|voltage|temperature|humidity|ppm)/.test(item.name) &&
						checkForStatisticError(value)
					) {
						value = 0;
						errorDatasets[d] = 1;
					}
					values.push(value);
					itemMin = Math.min(value || 0, itemMin);
					if (/(pv_setup_consumption)/.test(chartData.name) && item.stack) {
						const dt = data[d]?.dt;
						const val = value && item.visible ? value : 0;
						stackDataset[dt] = !stackDataset[dt] ? val : stackDataset[dt] + val;
						itemMax = Math.max(stackDataset[dt] || 0, itemMax);
					} else {
						itemMax = Math.max(value || 0, itemMax);
					}
				}
				if (
					item.disabled ||
					dataLen == 0 ||
					item.missingData === true ||
					((/(reversed|returned|export_energy|generated_energy|batt_invertor)/.test(
						item.name,
					) ||
						item.prevPeriod === true) &&
						values.find((a) => a) === undefined)
				) {
					continue;
				} else if (/consumption/.test(item.name) && values.find((a) => a) === undefined) {
					const reversed = chartData.series.find((i) =>
						/(reversed|returned|export_energy|generated_energy)/.test(i.name),
					);
					if (reversed && sum(reversed.data.map((i) => i.value)) !== 0) {
						continue;
					}
				}
				let fill: any = type.startsWith('area');
				let itemColor: ChartGradientColorType = {
					start: null,
					end: null,
					solid: null,
				};
				if (!item.colors) {
					itemColor = getGradientColor(item.style, this.types[this.currentTypeIndex]);
				}
				const hidden = !/^(tempSchedule|tariffIntervals)$/.test(chartData.name) && !item.visible;
				if (!hidden) {
					min = Math.min(min, itemMin);
					max = Math.max(max, itemMax);
				}
				datasets.push({
					data: values,
					label: formatSeriesLabel(
						`${item.prevPeriod ? 'previous_period_' : ''}${item.name}`,
						chartData,
					),
					name: item.name,
					hidden,
					backgroundColor: (data: ScriptableContext<ChartType>) => {
						if (type == 'bar' && this.showErrorPoint(data)) return 'red';
						else if (type == 'bar' && !itemColor.barGradientColor) return itemColor.solid;
						return item.colors ? item.colors : getChartColorStyle(data, itemColor);
					},
					labelColor: itemColor.labelIndicatorGradientColor ? itemColor : itemColor.solid,
					tooltipIndicatorColor:
						itemColor.tooltipIndicatorColor || (item.colors ? null : itemColor.solid),
					borderColor: (data) => {
						const index = data.dataIndex;
						let borderColorSolid = getIntervalAvatarColors(item.data[index]?.bgColor)?.solid;

						if (borderColorSolid) return borderColorSolid;

						return type != 'bar' && this.showErrorPoint(data)
							? 'red'
							: item.colors
								? item.colors
								: itemColor.solid;
					},
					borderWidth: (data) =>
						/^(pie|bar)$/.test(type) ? 0 : this.showErrorPoint(data) ? 1 : 2,
					pointBackgroundColor: (data) => {
						const index = data.dataIndex;
						let borderColorSolid = getIntervalAvatarColors(item.data[index]?.bgColor)?.solid;

						if (borderColorSolid) return borderColorSolid;

						return itemColor.solid;
					},
					hoverOffset: type == 'pie' ? 5 : 0,
					borderDash: item.prevPeriod || item.borderDash ? [8, 4] : [],
					hoverBorderWidth: type == 'pie' ? 3 : undefined,
					prevValues,
					metaData,
					consumptionCost,
					errorDatasets,
					maxBarThickness: 150,
					fill,
					stepped: type == 'areaspline' ? false : 'middle',
					pointRadius: (data) => (this.showErrorPoint(data) ? 3 : 0),
					spanGaps: false,
					clip: type == 'bar' ? { left: 5, top: false, right: -2, bottom: -0.5 } : false,
					pointStyle: 'circle',
					minBarLength: type == 'bar' ? 2 : 0,
					stack: type == 'bar' && item.stack ? item.stack : undefined,
					labelIndex: i,
					...(type == 'bar' && { borderRadius: 4 }),
					...(/^(tempSchedule|tariffIntervals)$/.test(chartData.name) && {
						segment: {
							backgroundColor: (ctx) => {
								const dataset = ctx.chart.data.datasets[ctx.datasetIndex];
								const dataIndex = ctx.p1DataIndex;

								if (dataIndex == null) return;

								// Recreate a partial ScriptableContext
								const fakeContext: ScriptableContext<ChartType> = {
									chart: ctx.chart,
									dataset,
									dataIndex,
									datasetIndex: ctx.datasetIndex,
									type: ctx.type,
									active: false,
									mode: '',
									parsed: undefined,
									raw: undefined,
								};

								const dataItem = data[dataIndex];
								if (dataItem?.bgColor) {
									const colorKey = dataItem.bgColor;

									if (!gradientCache[colorKey]) {
										const chartBgColor = getIntervalAvatarColors(colorKey);
										gradientCache[colorKey] = getChartColorStyle(
											fakeContext,
											chartBgColor,
										);
									}
									return gradientCache[colorKey];
								}
							},
							borderColor: (ctx) => {
								const index = ctx.p1DataIndex;
								let borderColorSolid = getIntervalAvatarColors(
									data[index]?.bgColor,
								)?.solid;

								if (borderColorSolid) {
									return borderColorSolid;
								}
							},
						},
					}),
				});
			}
		}
		let minMax =
			chartData.name == 'tariffIntervals'
				? { min: 0, max, stepSize: max / 2 }
				: this.calcTickInterval(min, max, type);
		if (/^valve_position$/.test(chartData.name)) {
			minMax = { min: 0, max: 100, stepSize: 50 };
		}
		return {
			data: {
				labels,
				datasets,
			},
			minMax,
		};
	}
	private setScaleOption(data): { x: any; y: any } {
		const type = this.types[this.currentTypeIndex],
			labelLength = data?.data?.labels?.length || 0,
			chartData = this.chartData,
			logarithmicType = Boolean(
				chartData.logarithmic && labelLength > 1 && data.minMax.stepSize >= 1000,
			);
		return {
			x: {
				offset: type == 'bar' || labelLength == 1,
				grid: {
					color: chartData.xGrid ? getSystemStyle(SystemStyles.COLOR_3) : 'transparent',
					tickColor: 'transparent',
				},
				ticks: {
					color: getSystemStyle(SystemStyles.COLOR_WHITE),
					align: 'center',
					font: {
						size: 10,
						weight: 400,
						family: getSystemStyle(SystemStyles.FONT_FAMILY),
					},
					callback: function (value, i) {
						value = this.getLabelForValue(value);
						if (chartData.name == 'dynamicChart') return value;
						else if (chartData.name == 'tariffIntervals' && chartData.rangeValue == 'week') {
							if (i % 48 != 0) return undefined;
							return dateFormat(value, getTimeLabelFormat('day_of_week'));
						} else if (chartData.name == 'tempSchedule' && chartData.rangeValue == 'week') {
							const dt = localTimeToUTC(value);
							if (dt.getHours() == 0 && dt.getMinutes() == 0) {
								return dateFormat(value, getTimeLabelFormat('day_of_week'));
							}
							return undefined;
						}
						let dFormat = dateFormat(value, getTimeLabelFormat(chartData.rangeValue), true);
						const tFormatUS = timeFormatUS();
						if (chartData.thumbMode) {
							if (chartData.rangeInterval == 'hour') {
								if (i % 4 != 0) return null;
								return (
									dateFormat(value, tFormatUS ? 'hA' : 'H', true) +
									(tFormatUS ? '' : get(format)('label_h_countdow', { default: 'h' }))
								).replace(/^0/, '');
							}
							if (chartData.rangeInterval == 'minute') {
								return i % 15 == 0 ? dFormat : null;
							}
							const len = chartData.series[0]?.data?.length || 0;
							return i == 0 || (len && i == len - 1) ? dFormat : null;
						} else if (
							!/^(tempSchedule|tariffIntervals|monthlyReport)$/.test(chartData.name) &&
							dFormat == '00:00'
						) {
							dFormat = dateFormat(value, getTimeLabelFormat('day_splitter'), true);
						} else if (
							/^(monthlyReport)$/.test(chartData.name) &&
							chartData.rangeValue == 'hour'
						) {
							return (
								dateFormat(value, tFormatUS ? 'hA' : 'H', true) +
								(tFormatUS ? '' : get(format)('label_h_countdow', { default: 'h' }))
							).replace(/^0/, '');
						}
						return dFormat;
					},
				},
			},
			y: {
				min: data.minMax ? data.minMax.min : 0,
				max: data.minMax ? data.minMax.max : 0,
				type: logarithmicType ? 'logarithmic' : undefined,
				grid: {
					color: getSystemStyle(SystemStyles.COLOR_3),
					borderColor: 'transparent',
					tickColor: 'transparent',
				},
				border: {
					color: 'transparent',
				},
				alignToPixels: true,
				autoSkip: !Boolean(logarithmicType),
				ticks: {
					autoSkipPadding: 20,
					color: getSystemStyle(SystemStyles.COLOR_WHITE),
					align: 'center',
					stepSize: data.minMax ? data.minMax.stepSize : 0,
					font: {
						size: 10,
						weight: 400,
						family: getSystemStyle(SystemStyles.FONT_FAMILY),
					},
					callback: function (value: number) {
						return formatDistanceUnit(
							convertToUnit(value, chartData.units, {
								fixed: 1,
								dontConvert: skipUnitConversion(chartData.title),
							}),
							chartData.deviceType,
						);
					},
				},
				...(logarithmicType && {
					afterBuildTicks: (chart) => {
						chart.ticks[chart.ticks.length - 1].major = true;
						return chart.ticks;
					},
				}),
			},
		};
	}
}
